import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import alert from '../utils/alertService';
import './Media.css';

const Media = () => {
  usePageTitle('Media');
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { isMedia, isFounder } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchMedia();
    if (isMedia || isFounder) {
      fetchEvents();
    }
  }, [isMedia, isFounder]);

  const fetchMedia = async () => {
    try {
      const response = await api.get('/media');
      setMedia(response.data);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (selectedEvent) {
        formData.append('event_id', selectedEvent);
      }

      await api.post('/media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      alert.success('File uploaded successfully! Waiting for approval');
      setSelectedFile(null);
      setSelectedEvent('');
      fetchMedia();
    } catch (error) {
      alert.error(error.response?.data?.message || 'Error uploading media');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  return (
    <div className="media-container">
      <h1>Media</h1>
      
      {isMedia && (
        <div className="upload-section">
          <h2>Upload New Media</h2>
          <form onSubmit={handleUpload}>
            <div className="form-group">
              <label>Select File (Image or Video)</label>
              <input
                type="file"
                accept="image/*,video/*"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                required
              />
            </div>
            <div className="form-group">
              <label>Event (Optional)</label>
              <select
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                <option value="">Select Event</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" disabled={uploading || !selectedFile} className="btn-primary">
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </form>
        </div>
      )}

      {media.length === 0 ? (
        <div className="no-data">
          <p>No media available at the moment.</p>
        </div>
      ) : (
        <div className="media-grid">
          {media.map((item) => {
            const getMediaUrl = () => {
              if (item.file_url) {
                return `${item.file_url}?t=${Date.now()}`;
              }
              if (item.file_path.startsWith('http')) {
                return `${item.file_path}?t=${Date.now()}`;
              }
              // Ensure the path doesn't have duplicate 'storage' or 'media'
              let path = item.file_path;
              if (path.startsWith('storage/')) {
                path = path.replace('storage/', '');
              }
              if (path.startsWith('media/')) {
                return `https://aliensteam.com/app-media/${path}?t=${Date.now()}`;
              }
              return `https://aliensteam.com/app-media/${path}?t=${Date.now()}`;
            };
            
            const mediaUrl = getMediaUrl();
            const defaultImages = [
              'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=250&fit=crop&q=80',
              'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=400&h=250&fit=crop&q=80',
              'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=400&h=250&fit=crop&q=80',
            ];
            const defaultImageIndex = (item.id - 1) % defaultImages.length;
            const defaultImage = defaultImages[defaultImageIndex];
            
            return (
            <div key={item.id} className="media-item">
              {item.file_type === 'image' ? (
                <img
                  src={mediaUrl}
                  alt="Media"
                  className="media-image"
                  onError={(e) => {
                    const currentSrc = e.target.src;
                    const retryCount = parseInt(e.target.getAttribute('data-retry') || '0');
                    console.log(`Media Item failed (retry ${retryCount}):`, currentSrc);
                    
                    if (retryCount < 2) {
                      e.target.setAttribute('data-retry', retryCount + 1);
                      if (currentSrc.includes('/storage/app/public/')) {
                        e.target.src = currentSrc.replace('/storage/app/public/', '/storage/');
                        return;
                      } 
                      if (currentSrc.includes('/storage/')) {
                        e.target.src = currentSrc.replace('/storage/', '/storage/app/public/');
                        return;
                      }
                    }

                    console.error('Error loading media:', currentSrc, 'for item:', item.id);
                    if (currentSrc !== defaultImage) {
                      e.target.src = defaultImage;
                    } else {
                      // Show placeholder if default image also fails
                      e.target.style.display = 'none';
                      const placeholder = e.target.nextElementSibling;
                      if (placeholder && placeholder.classList.contains('media-placeholder')) {
                        placeholder.style.display = 'flex';
                      }
                    }
                  }}
                />
              ) : (
                <video
                  src={mediaUrl}
                  controls
                  className="media-video"
                  poster={defaultImage}
                  onError={(e) => {
                    console.error('Error loading video:', mediaUrl, 'for item:', item.id);
                    e.target.poster = defaultImage;
                  }}
                />
              )}
              <div className="media-placeholder" style={{ display: 'none' }}>
                <div className="placeholder-icon">🖼️</div>
                <div className="placeholder-text">Image not available</div>
              </div>
              {item.event && (
                <div className="media-event">📅 Event: {item.event.title}</div>
              )}
              {item.user && (
                <div className="media-user">👤 {item.user.name}</div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Media;
